import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Role } from '@arcnet/contracts';
import { api } from './client.js';
import { tmsKeys } from './hooks-tms.js';

/**
 * Roles (docs/33): the built-in ones, and custom ones the platform, a reseller or a client makes.
 * Every rule is the API's; these only ask and refresh.
 */

interface RoleScope { tenantId?: string | undefined; resellerId?: string | undefined }

export const roleKeys = {
  all: ['roles'] as const,
  list: (s: RoleScope) => ['roles', s.tenantId ?? '', s.resellerId ?? ''] as const,
};

/** The roles the signed-in person can see; with a tenant or reseller, those that may be given there. */
export function useRoles(scope: RoleScope = {}) {
  const q = new URLSearchParams();
  if (scope.tenantId) q.set('tenant_id', scope.tenantId);
  if (scope.resellerId) q.set('reseller_id', scope.resellerId);
  const suffix = q.toString() ? `?${q}` : '';
  return useQuery({
    queryKey: roleKeys.list(scope),
    queryFn: () => api.get<{ data: Role[] }>(`/v1/roles${suffix}`),
  });
}

export interface RoleDraft { name: string; description: string | null; permissions: string[] }

/** A client's own role, for its own staff. */
export function useCreateTenantRole(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RoleDraft) => api.post<Role>(`/v1/tenants/${tenantId}/roles`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: roleKeys.all }); },
  });
}

/** A platform role, or a reseller's when resellerId is set. */
export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RoleDraft & { scope_level: 'platform' | 'reseller' | 'tenant'; reseller_id: string | null }) =>
      api.post<Role>('/v1/roles', body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: roleKeys.all }); },
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: RoleDraft & { id: string }) => api.put<Role>(`/v1/roles/${id}`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: roleKeys.all }); },
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/v1/roles/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: roleKeys.all }); },
  });
}

/** Changing a person's role on an account, in place. */
export function useChangeMembershipRole(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, roleId }: { membershipId: string; roleId: string }) =>
      api.put<{ role_name: string }>(`/v1/memberships/${membershipId}`, { role_id: roleId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tmsKeys.users(tenantId) });
      void qc.invalidateQueries({ queryKey: roleKeys.all });
    },
  });
}
